import { useEffect, useMemo, useRef, useState } from "react";
import { getAuthToken, syncApiUrl } from "@cliqsuite/db/powersync";
import type { BusinessHours } from "@cliqsuite/db/schemas";
import { getRows, insertRow, updateRow, useLive, useRows } from "../lib/db";
import type { SignedInStaff } from "../types";
import { checkStaffGeofence, type GeofenceReason } from "../lib/geofence";
import { usePosShapeSyncReady } from "../components/PosDbProvider";

/** Full-screen-blocking reasons — the till can't tell either way, so
 * (unlike "too_far", a plain inline message) these need actual instructions,
 * not just a retry button. */
export type LocationBlockReason = Extract<GeofenceReason, "denied" | "unavailable" | "timeout">;
/** Clock out: stamps the end and the shift's length in minutes (a local write, uploaded when online). */
async function endShift(sessionId: string, clockedOutAt: string): Promise<void> {
  const [row] = await getRows("staff_sessions", "SELECT * FROM staff_sessions WHERE id = $1", [sessionId]);
  const minutes = row ? Math.max(0, Math.round((new Date(clockedOutAt).getTime() - new Date(row.clocked_in_at).getTime()) / 60000)) : null;
  await updateRow("staff_sessions", sessionId, { clocked_out_at: clockedOutAt, duration_minutes: minutes, updated_at: clockedOutAt });
}

const BLOCKING_REASONS: readonly GeofenceReason[] = ["denied", "unavailable", "timeout"];
function isLocationBlockReason(reason: GeofenceReason): reason is LocationBlockReason {
  return BLOCKING_REASONS.includes(reason);
}

/** How many consecutive out-of-range checks (GEOFENCE_CHECK_INTERVAL_MS
 * apart) before auto clock-out fires — a single reading could just be a GPS
 * hiccup, and ending someone's shift on their behalf is disruptive enough
 * to be worth a moment's confirmation first. */
const GEOFENCE_CHECK_INTERVAL_MS = 60 * 1000;
const GEOFENCE_CONFIRM_STREAK = 2;

/** Auto sign-out (and thus clock-out, via the pairing below) after this much
 * till inactivity — covers staff who walk away without hitting "Sign out". */
const IDLE_TIMEOUT_MS = 25 * 60 * 1000;

const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

// A page reload doesn't just happen from a manual refresh — vite-plugin-pwa
// can swap in a new service worker and reload out from under an active
// till at any time (see the PWA update banner in RootLayout, which now asks
// before reloading — but this covers every OTHER reload cause too: a
// crashed tab, a flaky network reconnect, a browser update). signedIn/
// sessionId are otherwise plain in-memory state, so any of those would
// silently boot a mid-shift staff member back to the PIN screen. This
// doesn't touch the DB (their staff_sessions row stays open either way —
// see the reattach logic in verifyPin), but forcing a re-PIN mid-order is
// still a real interruption, so the sign-in itself is restored on reload.
const SESSION_STORAGE_PREFIX = "cliqsuite:pos:staffSession:";

// sessionId is nullable here — a staff member can be signed in to the till
// without an open shift (manually clocked out, or auto clocked out for
// drifting out of range, without also being signed out of the till itself;
// see clockOut() below), and that state needs to survive a reload too.
function loadSavedSession(storeId: string): { staffId: string; sessionId: string | null } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_PREFIX + storeId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { staffId?: unknown; sessionId?: unknown };
    if (typeof parsed.staffId === "string" && (typeof parsed.sessionId === "string" || parsed.sessionId === null)) {
      return { staffId: parsed.staffId, sessionId: parsed.sessionId as string | null };
    }
  } catch {
    // sessionStorage unavailable (private mode) or corrupt value — just skip rehydration.
  }
  return null;
}
function saveSession(storeId: string, staffId: string, sessionId: string | null) {
  try {
    sessionStorage.setItem(SESSION_STORAGE_PREFIX + storeId, JSON.stringify({ staffId, sessionId }));
  } catch {
    // Sign-in still works this run — it just won't survive a reload.
  }
}
function clearSavedSession(storeId: string) {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_PREFIX + storeId);
  } catch {
    // ignore
  }
}

function toMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Is `now` inside today's actual open/close window? Used to suppress the
 * idle auto-clockout below — a quiet stretch with no till activity during
 * business hours is normal (nothing to do), not a sign someone forgot to
 * sign out; the latter is what the timeout exists to catch, and that only
 * really happens after closing. Unconfigured/closed days count as outside. */
function isWithinOperatingHours(businessHours: BusinessHours | null | undefined, now: Date): boolean {
  // No hours configured at all yet — every new store starts this way
  // (business_hours: {}, see FirstStoreGate.tsx/StoresTab.tsx's create
  // defaults) until an owner visits Store Settings. There's no basis to
  // guess open vs. closed here, so default to "open" (suppresses the idle
  // auto-signout below) rather than "closed" — the previous behavior
  // auto-signed staff out of a fresh, default-configured store after any
  // 25-minute lull, all day every day, which is exactly the "should never
  // happen" report this fixes. Matches clockInWindowError's own existing
  // "unconfigured means unrestricted" stance a few lines below.
  if (!businessHours || Object.keys(businessHours).length === 0) return true;
  const dayKey = DOW_KEYS[now.getDay()] ?? "sun";
  const today = businessHours[dayKey];
  if (!today || today.closed) return false;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = toMinutes(today.open);
  const closeMin = toMinutes(today.close);
  if (openMin === null || closeMin === null) return false;
  return nowMin >= openMin && nowMin <= closeMin;
}

/** New clock-ins only — never blocks clock-out (overtime is expected).
 * Returns an error message if now falls outside today's clock-in window, or
 * null if it's fine (including when hours aren't configured for today at all). */
function clockInWindowError(businessHours: BusinessHours | null | undefined, now: Date): string | null {
  const dayKey = DOW_KEYS[now.getDay()] ?? "sun";
  const today = businessHours?.[dayKey];
  if (!today) return null;
  if (today.closed) return "Store is closed today — clock-in isn't available.";
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const openMin = toMinutes(today.staff_start || today.open);
  const closeMin = toMinutes(today.close);
  if (openMin !== null && nowMin < openMin) {
    return `Too early — clock-in opens at ${today.staff_start || today.open}.`;
  }
  if (closeMin !== null && nowMin > closeMin) {
    return `Store's closed for the day — clock-in isn't available after ${today.close}.`;
  }
  return null;
}

/** Staff PIN gate phase 2 & 3: pick a roster member, verify their PIN server-side, and log real clock in/out. */
export function useStaffSession(
  storeId: string | null,
  deviceId: string | null = null,
  /** Platform-owner debugging only — see core.devices.bypass_geofence's own
   * doc. Skips the new-clock-in block AND the periodic auto-clock-out poll. */
  bypassGeofence = false,
) {
  // Unconditionally active-only, store-filtered only when storeId is known
  // (same "fetch broad, narrow in one query" shape the original TanStack DB
  // version had) — a single null-safe predicate rather than two branches.
  const staffResult = useRows("staff", "SELECT * FROM staff WHERE is_active = true AND ($1 IS NULL OR store_id = $1)", [storeId]);
  // Gated on staff's own sync signal, not just the local query resolving —
  // see PosDbProvider.tsx's usePosShapeSyncReady doc for why: a local query
  // resolves the instant PGlite runs it against a still-possibly-empty
  // local table, independent of whether the real remote sync has actually
  // landed the roster yet. Deliberately the staff-specific signal, not the
  // broader usePosSyncReady() (all 13 shapes) — that one previously gated
  // this, and an unrelated slow shape (products, orders, ...) could flip it
  // true via the 20s safety-net timeout before staff itself had actually
  // landed, making a freshly paired till's roster look genuinely empty.
  const staffSynced = usePosShapeSyncReady("staff");
  const staffReady = staffSynced && !!staffResult;
  const roster = useMemo(() => staffResult?.rows ?? [], [staffResult]);

  // Open sessions for this store — used to re-attach to an already-open
  // session on sign-in instead of creating a duplicate (double PIN entry,
  // or sessionId having been lost to a reload).
  const { data: storeSessions, isReady: sessionsReady } = useLive("staff_sessions", "SELECT * FROM staff_sessions WHERE store_id = $1", [storeId ?? ""]);

  const storesResult = useRows("stores", "SELECT * FROM stores WHERE id = $1", [storeId ?? ""]);
  // Deliberately NOT gated on posSynced, unlike staffReady above — this
  // feeds useStoreLocationGate, which blocks the entire login flow (staff
  // picker + PIN) until storeReady flips true. That gate already shows an
  // honest "Confirming this device's location…" state while waiting, never
  // a false/misleading one, so it doesn't need posSynced's full
  // cross-shape signal — and requiring it introduced a real regression:
  // posSynced only flips once ALL 8 of this app's shapes finish their
  // initial sync (products, categories, variant groups/options, tables,
  // businesses, stores, staff), most of which have nothing to do with
  // location checking. On a real network, any one of those being slow
  // (the menu catalog, in particular) stuck the whole app at "Confirming
  // this device's location…" indefinitely. storesResult resolving is
  // enough here — the local table only needs this store's own row, which
  // syncs early and independently of the rest.
  const storeReady = !!storesResult;
  const storeRows = storesResult?.rows;
  const businessHours = storeRows?.[0]?.business_hours as BusinessHours | undefined;

  const [pickedId, setPickedId] = useState<string | null>(null);
  const [signedIn, setSignedIn] = useState<SignedInStaff | null>(null);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [clockingIn, setClockingIn] = useState(false);
  // Full-screen-blocking outcome from the strict geofence check below (set
  // by either verifyPin — a fresh sign-in — or clockIn — resuming a shift
  // someone's already signed in to, no PIN needed). Deliberately separate
  // from the plain `error` string: "too_far" is a normal, textual "come
  // stand at the store" message, but these need real instructions, since a
  // staff member who was signed in before this shipped and never had
  // location permission would otherwise be stuck with no way to fix it.
  const [locationBlocked, setLocationBlocked] = useState<LocationBlockReason | null>(null);

  const picked = roster.find((s) => s.id === pickedId) ?? null;

  const GEO_MESSAGE: Record<Exclude<GeofenceReason, "ok" | "no_store_location">, string> = {
    too_far: "You need to be at the store to clock in.",
    denied: "Location access is required to clock in.",
    unavailable: "This device can't check location.",
    timeout: "Couldn't confirm your location — try again.",
  };

  // Blocks a NEW clock-in outright — same "new clock-ins only" policy as
  // clockInWindowError (resuming an already-open shift is always let
  // through, since they're already working). As strict as the geofence
  // check itself now gets: only a store with no location configured at all
  // (feature not enabled) passes through untouched — denied/unavailable/
  // too-far/timeout all block a fresh clock-in, unlike the background
  // auto-clock-out poller below, which still only ever acts on a
  // confirmed too-far reading (see checkStaffGeofence's own doc for why).
  async function geofenceBlockError(): Promise<{ message: string; reason: GeofenceReason } | null> {
    if (bypassGeofence) return null;
    const store = storeRows?.[0];
    if (!store || store.latitude == null || store.longitude == null) return null;
    const result = await checkStaffGeofence(store);
    if (result.reason === "ok" || result.reason === "no_store_location") return null;
    return { message: GEO_MESSAGE[result.reason], reason: result.reason };
  }

  function clearLocationBlocked() {
    setLocationBlocked(null);
  }

  /** "Try again" from the full-screen blocker. There's no PIN to resubmit
   * once verifyPin has already consumed it, so a block that happened
   * mid-sign-in just dismisses back to the PIN pad — but a block from
   * clockIn() (already signed in, no PIN involved) can safely re-run the
   * exact same check. */
  function retryLocationCheck() {
    setLocationBlocked(null);
    if (signedIn && !sessionId) clockIn();
  }

  // Rehydrate a saved sign-in once, per store, once both live queries have
  // actually finished their initial sync (isReady — `data` itself turns into
  // a, possibly still-empty, array well before that, so gating on `data`
  // being defined let this fire against a half-synced collection, decide
  // the saved session was gone, clear it, and never retry — the exact bug
  // that meant staff kept getting re-PIN'd on reload despite this existing).
  // Only restores if that exact session is still open server-side, so a
  // manual clock-out from Owner Portal while this till was reloading is
  // respected, not overridden.
  const rehydratedForStore = useRef<string | null>(null);
  useEffect(() => {
    if (!storeId || signedIn) return;
    if (rehydratedForStore.current === storeId) return;
    if (!staffReady || !sessionsReady) return;
    rehydratedForStore.current = storeId;
    const saved = loadSavedSession(storeId);
    if (!saved) return;
    const member = roster.find((s) => s.id === saved.staffId);
    if (!member) {
      clearSavedSession(storeId);
      return;
    }
    if (saved.sessionId === null) {
      // Signed in but not clocked in — a manual clock-out, or an
      // out-of-range auto clock-out, that deliberately kept them on the
      // till (see clockOut() below). No open session to validate here;
      // being on the roster is enough to restore this.
      setSignedIn({
        id: member.id,
        name: member.name,
        role: member.role,
        avatarColor: member.avatar_color,
        isManager: member.is_manager,
      });
      setSessionId(null);
      return;
    }
    const openSession = (storeSessions ?? []).find(
      (s) => s.id === saved.sessionId && s.staff_id === saved.staffId && s.clocked_out_at === null,
    );
    if (openSession) {
      setSignedIn({
        id: member.id,
        name: member.name,
        role: member.role,
        avatarColor: member.avatar_color,
        isManager: member.is_manager,
      });
      setSessionId(openSession.id);
    } else {
      clearSavedSession(storeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, signedIn, staffReady, sessionsReady, storeSessions, roster]);

  function pick(id: string) {
    setPickedId(id);
    setError("");
  }

  function backToRoster() {
    setPickedId(null);
    setError("");
  }

  async function verifyPin(pin: string) {
    if (!picked) return;
    setVerifying(true);
    try {
      const res = await fetch(syncApiUrl(`/core/staff/${picked.id}/verify-pin`), {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${getAuthToken() ?? ""}` },
        body: JSON.stringify({ pin }),
      });
      const body = (await res.json()) as { ok: boolean };
      if (body.ok) {
        // Re-attach to an already-open session for this person instead of
        // opening a second one — covers signing in twice in a row and a
        // reload losing the in-memory sessionId. Only a genuinely NEW
        // clock-in is subject to the store's hours — someone resuming an
        // already-open shift (or clocking out, which never checks this) is
        // always let through, since they're already working.
        const existingOpen = (storeSessions ?? []).find((s) => s.staff_id === picked.id && s.clocked_out_at === null);
        if (!existingOpen) {
          const windowError = clockInWindowError(businessHours, new Date());
          if (windowError) {
            setError(windowError);
            setVerifying(false);
            return;
          }
          const geoError = await geofenceBlockError();
          if (geoError) {
            if (isLocationBlockReason(geoError.reason)) setLocationBlocked(geoError.reason);
            else setError(geoError.message);
            setVerifying(false);
            return;
          }
        }
        setSignedIn({
          id: picked.id,
          name: picked.name,
          role: picked.role,
          avatarColor: picked.avatar_color,
          isManager: picked.is_manager,
        });
        setError("");
        setPickedId(null);
        if (storeId) {
          if (existingOpen) {
            setSessionId(existingOpen.id);
            saveSession(storeId, picked.id, existingOpen.id);
          } else {
            const id = crypto.randomUUID();
            setSessionId(id);
            const now = new Date().toISOString();
            void insertRow("staff_sessions", {
              id,
              staff_id: picked.id,
              store_id: storeId,
              device_id: deviceId,
              clocked_in_at: now,
              clocked_out_at: null,
              duration_minutes: null,
              break_minutes: 0,
              source: "pos",
              notes: null,
              created_at: now,
              updated_at: now,
            });
            saveSession(storeId, picked.id, id);
          }
        }
      } else {
        setError("That PIN didn't match. Try again.");
      }
    } catch {
      setError("Couldn't reach the server — try again.");
    } finally {
      setVerifying(false);
    }
  }

  function signOut(options?: { clockOut?: boolean; clearSaved?: boolean }) {
    const { clockOut = true, clearSaved = true } = options ?? {};
    if (clockOut && sessionId) {
      const clockedOutAt = new Date().toISOString();
      void endShift(sessionId, clockedOutAt);
    }
    if (clearSaved && storeId) clearSavedSession(storeId);
    if (!clearSaved && storeId && signedIn && sessionId) saveSession(storeId, signedIn.id, sessionId);
    setSessionId(null);
    setSignedIn(null);
    setPickedId(null);
    setError("");
  }

  /** Ends the current shift (staff_sessions.clocked_out_at) WITHOUT signing
   * out of the till — unlike signOut(), which always does both. Used by the
   * manual "Clock Out" button in Settings, and by the geofence check below,
   * so a staff member who's just stepped out (errand, delivery, break) can
   * clockIn() again without re-entering their PIN. */
  function clockOut() {
    if (!sessionId) return;
    const clockedOutAt = new Date().toISOString();
    void endShift(sessionId, clockedOutAt);
    setSessionId(null);
    if (storeId && signedIn) saveSession(storeId, signedIn.id, null);
  }

  /** Resumes a shift for whoever's already signed in to the till — no PIN
   * needed, since they never signed out of the till itself, just clocked
   * out of their previous shift. Same geofence + hours gating as a fresh
   * PIN clock-in, since this creates a new shift the same way. */
  function clockIn() {
    if (!signedIn || !storeId || sessionId) return;
    const windowError = clockInWindowError(businessHours, new Date());
    if (windowError) {
      setError(windowError);
      return;
    }
    setClockingIn(true);
    void (async () => {
      try {
        const geoError = await geofenceBlockError();
        if (geoError) {
          if (geoError.reason === "too_far") setError(geoError.message);
          else setLocationBlocked(geoError.reason as LocationBlockReason);
          return;
        }
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        void insertRow("staff_sessions", {
          id,
          staff_id: signedIn.id,
          store_id: storeId,
          device_id: deviceId,
          clocked_in_at: now,
          clocked_out_at: null,
          duration_minutes: null,
          break_minutes: 0,
          source: "pos",
          notes: null,
          created_at: now,
          updated_at: now,
        });
        setSessionId(id);
        saveSession(storeId, signedIn.id, id);
        setError("");
      } finally {
        setClockingIn(false);
      }
    })();
  }

  // Auto clock-out (NOT a full sign-out — see clockOut() above) once this
  // device is confirmed out of range of the store for GEOFENCE_CONFIRM_STREAK
  // consecutive checks. Safety net for a forgotten manual clock-out; only
  // ends the shift, so staff can clockIn() again the moment they're back
  // without re-entering their PIN. Only active once the store has a
  // latitude/longitude set (see StoreSettingsTab.tsx) — outOfRange reads
  // null (never true) with no location configured, no geolocation support,
  // or a denied/failed permission, so this can never fire from a GPS hiccup
  // or an unconfigured store.
  // Depends on the lat/lng primitives, not the storeRows array itself —
  // that reference changes on every unrelated store field edit (tax rate,
  // hours, ...), which would otherwise restart this interval (and reset the
  // confirm streak below) far more often than the store's actual location
  // ever changes.
  const storeLat = storeRows?.[0]?.latitude ?? null;
  const storeLon = storeRows?.[0]?.longitude ?? null;
  const storeGeofenceRadiusM = storeRows?.[0]?.geofence_radius_m ?? null;
  const outOfRangeStreakRef = useRef(0);
  useEffect(() => {
    // bypassGeofence: platform-owner debugging (see core.devices.
    // bypass_geofence's own doc) — without this, a debug session signed in
    // from off-site would get silently auto-clocked-out mid-use.
    if (bypassGeofence || !sessionId || storeLat == null || storeLon == null) {
      outOfRangeStreakRef.current = 0;
      return;
    }
    let cancelled = false;
    const check = async () => {
      const result = await checkStaffGeofence({ latitude: storeLat, longitude: storeLon, geofence_radius_m: storeGeofenceRadiusM });
      if (cancelled) return;
      if (result.outOfRange === true) {
        outOfRangeStreakRef.current += 1;
        if (outOfRangeStreakRef.current >= GEOFENCE_CONFIRM_STREAK) {
          outOfRangeStreakRef.current = 0;
          clockOut();
        }
      } else {
        outOfRangeStreakRef.current = 0;
      }
    };
    void check();
    const interval = setInterval(() => void check(), GEOFENCE_CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bypassGeofence, sessionId, storeLat, storeLon, storeGeofenceRadiusM]);

  useEffect(() => {
    return () => {
      if (storeId && signedIn && sessionId) {
        saveSession(storeId, signedIn.id, sessionId);
      }
    };
  }, [storeId, signedIn, sessionId]);

  // Auto sign-out (and clock-out) after IDLE_TIMEOUT_MS of no till activity —
  // the actual fix for "forgot to log out": the till self-corrects instead
  // of leaving a shift open indefinitely. Any pointer/keyboard/wheel event
  // anywhere in the app resets the clock.
  const lastActivityRef = useRef(Date.now());
  useEffect(() => {
    if (!signedIn) return;
    lastActivityRef.current = Date.now();
    const bump = () => {
      lastActivityRef.current = Date.now();
    };
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current <= IDLE_TIMEOUT_MS) return;
      if (isWithinOperatingHours(businessHours, new Date())) return;
      signOut();
    }, 30_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, businessHours]);

  return {
    roster,
    staffReady,
    picked,
    signedIn,
    error,
    verifying,
    pick,
    backToRoster,
    verifyPin,
    signOut,
    clockedIn: sessionId !== null,
    clockingIn,
    clockIn,
    clockOut,
    locationBlocked,
    retryLocationCheck,
    clearLocationBlocked,
    store: storeRows?.[0] ?? null,
    storeReady,
  };
}
